<template>
  <div class="admin-page">
    <admin-page-header
      eyebrow="Paraşüt"
      :title="isSales ? 'Satış Faturaları' : 'Giderler'"
      :description="isSales ? 'Satış faturalarını, müşteri, kategori, etiket ve tahsilat bilgileriyle inceleyin.' : 'Giderleri, tedarikçi, kategori, etiket ve ödeme bilgileriyle inceleyin.'"
    >
      <template #actions>
        <v-btn
          small
          text
          :to="{ name: 'garantiAccount' }"
        >
          Garanti Hesabı
        </v-btn>
        <v-btn
          small
          outlined
          color="primary"
          :loading="loading"
          @click="refresh"
        >
          Yenile
        </v-btn>
      </template>
    </admin-page-header>
    <v-alert
      v-if="error"
      type="error"
      outlined
      dense
    >
      {{ error }}
    </v-alert>
    <v-alert
      v-if="!loading && !error && !status.connected"
      type="info"
      outlined
    >
      Paraşüt bağlantısı gerekli. Bağlantıyı Garanti Hesabı sayfasından yetkilendirin.
    </v-alert>
    <template v-if="status.connected">
      <v-tabs
        v-if="!isSales"
        :value="expenseSource"
        class="mb-4"
        @change="changeSource"
      >
        <v-tab href="#bills">
          Faturalar / Fişler
        </v-tab>
        <v-tab href="#salaries">
          Maaş / Prim
        </v-tab>
        <v-tab href="#taxes">
          Vergi / SGK
        </v-tab>
      </v-tabs>
      <p class="text-caption text--secondary">
        Salt okunur · Şirket #{{ status.companyId }} · {{ totalCount }} belge
        · Son başarılı okuma: {{ formatReadAt(readAt) }} (Türkiye saati)
      </p>
      <v-card
        class="admin-table-card"
        outlined
      >
        <v-form
          class="admin-page-toolbar supplier-toolbar"
          @submit.prevent="applyFilters"
        >
          <v-text-field
            v-if="isSales || expenseSource === 'bills'"
            v-model="filters.supplierId"
            :label="isSales ? 'Müşteri ID (isteğe bağlı)' : 'Tedarikçi ID (isteğe bağlı)'"
            outlined
            dense
            hide-details
            clearable
            :disabled="loading"
          />
          <v-text-field
            v-model="filters.issueDate"
            label="Belge günü (isteğe bağlı)"
            type="date"
            outlined
            dense
            hide-details
            clearable
            :disabled="loading"
          />
          <v-select
            v-if="isSales || expenseSource === 'bills'"
            v-model="filters.itemType"
            :items="itemTypes"
            label="Belge türü"
            outlined
            dense
            hide-details
            :disabled="loading"
          />
          <v-btn
            type="submit"
            small
            outlined
            color="primary"
            :disabled="loading"
          >
            Uygula
          </v-btn>
        </v-form>
        <v-data-table
          :headers="headers"
          :items="bills"
          :loading="loading"
          :server-items-length="totalCount"
          :page="page"
          :items-per-page="size"
          :expanded.sync="expanded"
          item-key="id"
          show-expand
          :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
          class="admin-data-table"
          disable-sort
          @update:page="changePage"
          @update:items-per-page="changeSize"
        >
          <template #item.invoiceNo="{ item }">
            <strong>{{ item.invoiceNo || `Belge #${item.id}` }}</strong>
            <div class="text-caption text--secondary supplier-description">
              {{ item.description || 'Açıklama yok' }}
            </div>
            <span
              v-if="item.archived"
              class="text-caption"
            >Arşivlenmiş</span>
            <div class="document-tags">
              <v-chip
                x-small
                outlined
              >
                {{ documentTypeLabel(item.itemType) }}
              </v-chip>
            </div>
          </template>
          <template #item.tags="{ item }">
            <div class="document-tags">
              <v-chip
                v-if="item.category"
                small
                outlined
                color="primary"
                :title="`Kategori #${item.category.id}`"
              >
                {{ item.category.name || `Kategori #${item.category.id}` }}
              </v-chip>
              <v-chip
                v-for="tag in item.tags || []"
                :key="tag.id"
                small
                outlined
                :title="`Etiket #${tag.id}`"
              >
                {{ tag.name || `Etiket #${tag.id}` }}
              </v-chip>
              <span
                v-if="!item.category && !(item.tags || []).length"
                class="text-caption text--secondary"
              >Etiket yok</span>
            </div>
          </template>
          <template #item.payeeName="{ item }">
            {{ item.payeeName || '-' }}
            <div class="text-caption text--secondary">
              {{ item.payeeType === 'employees' ? 'Çalışan' : item.payeeType === 'contacts' ? 'Cari hesap' : '' }}{{ item.supplierId ? ` · #${item.supplierId}` : '' }}
            </div>
          </template>
          <template #item.issueDate="{ item }">
            {{ formatDay(item.issueDate) }}
          </template>
          <template #item.dueDate="{ item }">
            {{ formatDay(item.dueDate) }}
          </template>
          <template #item.paymentStatus="{ item }">
            <v-chip
              small
              outlined
            >
              {{ paymentStatusLabel(item.paymentStatus) }}
            </v-chip>
          </template>
          <template #item.total="{ item }">
            <span class="money">{{ formatMoney(item.total, item.currency) }}</span>
          </template>
          <template #item.paid="{ item }">
            <span class="money">{{ formatMoney(item.paid, item.currency) }}</span>
          </template>
          <template #item.remaining="{ item }">
            <span class="money">{{ formatMoney(item.remaining, item.currency) }}</span>
          </template>
          <template #expanded-item="{ headers: columns, item }">
            <td
              :colspan="columns.length"
              class="pa-4"
            >
              <h3 class="text-subtitle-1 mb-2">
                Belge #{{ item.id }} · {{ isSales ? 'Tahsilat geçmişi' : 'Ödeme geçmişi' }}
              </h3>
              <v-simple-table
                v-if="item.payments.length"
                dense
              >
                <thead><tr><th>Ödeme ID</th><th>Tarih</th><th>Tutar</th><th>İşlem ID</th><th>Not</th></tr></thead>
                <tbody>
                  <tr
                    v-for="payment in item.payments"
                    :key="payment.id"
                  >
                    <td>#{{ payment.id }}</td><td>{{ formatDay(payment.date) }}</td>
                    <td class="money">
                      {{ formatMoney(payment.amount, payment.currency) }}
                    </td>
                    <td>{{ payment.transactionId || '-' }}</td>
                    <td>{{ payment.detailsAvailable ? (payment.notes || '-') : 'Paraşüt ödeme detayını döndürmedi.' }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
              <p
                v-else
                class="mb-0"
              >
                Bu belgeye bağlı ödeme/tahsilat kaydı dönmedi; cari hesabın bağımsız hareketleri bu listede yer almayabilir.
              </p>
            </td>
          </template>
          <template #no-data>
            {{ error ? 'Veri alınamadı. Yenile ile tekrar deneyin.' : 'Bu filtrelerle belge bulunamadı.' }}
          </template>
        </v-data-table>
        <p class="text-caption text--secondary px-4 py-3 mb-0">
          Her satır bir belgedir; ödeme/tahsilat geçmişi için satırı açın. Paraşüt kategori ve etiketleri aynen gösterilir.
          Tüm hesapları kapsar, Garanti ile sınırlı değildir. Geçmiş kayıt sınırı yoktur; diğer sayfalara geçebilirsiniz.
          {{ isSales ? 'Fatura, iade ve ihracat kayıtları gösterilir; teklifler dahil değildir. Tahsil edilen tutar, genel toplamdan kalan tutar çıkarılarak hesaplanır.' : 'Belge türü seçilmezse Paraşüt gider listesinin tamamı sorgulanır.' }}
          Bu ekrandan ödeme yapılmaz; Adverport ödeme talepleri değişmez.
          <span v-if="!isSales">Banka giderlerinin toplu listesi API tarafından sunulmuyor; Garanti hareketlerinde banka masrafı olarak görüntülenir.</span>
        </p>
      </v-card>
    </template>
  </div>
</template>

<script>
import axios from 'axios'
import { getAccessToken } from '@/utils/panelSession'
import { resolveApiErrorMessage } from '@/utils/apiErrors'
import AdminPageHeader from '@/components/admin/AdminPageHeader.vue'
import {
  formatParasutReadAt, formatParasutMoney, formatParasutDay, paymentStatusLabel, documentTypeLabel,
} from './parasutPresentation'

export default {
  components: { AdminPageHeader },
  props: { kind: { type: String, default: 'expenses', validator: value => ['expenses', 'sales-invoices'].includes(value) } },
  data: () => ({
    status: {},
    bills: [],
    totalCount: 0,
    readAt: null,
    loading: false,
    error: '',
    requestId: 0,
    page: 1,
    size: 25,
    expanded: [],
    expenseSource: 'bills',
    filters: { supplierId: '', issueDate: '', itemType: null },
    appliedFilters: {},
  }),
  computed: {
    isSales() { return this.kind === 'sales-invoices' },
    endpoint() { return !this.isSales && this.expenseSource !== 'bills' ? `expenses/${this.expenseSource}` : this.kind },
    itemTypes() {
      return [{ text: 'Tümü', value: null }, ...(this.isSales
        ? [{ text: 'Satış faturası', value: 'invoice' }, { text: 'İhracat', value: 'export' }, { text: 'İade', value: 'refund' }]
        : [{ text: 'Gider', value: 'purchase_bill' }, { text: 'İade', value: 'refund' }, { text: 'İptal', value: 'cancelled' }])]
    },
    headers() {
      return [
        { text: 'Belge / açıklama', value: 'invoiceNo' }, { text: this.isSales ? 'Müşteri' : 'Tedarikçi / alıcı', value: 'payeeName' },
        { text: 'Kategori / etiketler', value: 'tags', width: 210 },
        { text: 'Belge tarihi', value: 'issueDate', width: 120 }, { text: 'Vade', value: 'dueDate', width: 120 },
        { text: this.isSales ? 'Tahsilat durumu' : 'Ödeme durumu', value: 'paymentStatus', width: 155 },
        { text: 'Genel toplam', value: 'total', align: 'end' }, { text: this.isSales ? 'Tahsil edilen' : 'Ödenen', value: 'paid', align: 'end' },
        { text: 'Kalan', value: 'remaining', align: 'end' },
      ]
    },
  },
  watch: {
    kind() {
      this.filters = { supplierId: '', issueDate: '', itemType: null }
      this.appliedFilters = {}
      this.expenseSource = 'bills'
      this.page = 1
      this.refresh()
    },
  },
  mounted() { this.refresh() },
  beforeDestroy() { this.requestId += 1 },
  methods: {
    async refresh() {
      this.requestId += 1
      const id = this.requestId
      this.loading = true
      this.error = ''
      this.bills = []
      this.totalCount = 0
      this.readAt = null
      this.expanded = []
      const options = { headers: { Authorization: `Bearer ${getAccessToken()}` } }
      const base = `${process.env.VUE_APP_BASE_API_URL}admin/parasut/`
      try {
        const status = await axios.get(`${base}status`, options)
        if (id !== this.requestId) return
        this.status = status.data.data
        if (!this.status.connected) return
        let response = await axios.get(`${base}${this.endpoint}`, { ...options, params: { ...this.appliedFilters, page: this.page, size: this.size } })
        if (id !== this.requestId) return
        const lastPage = response.data.data.bills.totalPages
        if (lastPage > 0 && this.page > lastPage) {
          this.page = lastPage
          response = await axios.get(`${base}${this.endpoint}`, { ...options, params: { ...this.appliedFilters, page: this.page, size: this.size } })
          if (id !== this.requestId) return
        }
        this.bills = response.data.data.bills.items
        this.totalCount = response.data.data.bills.totalCount
        this.readAt = response.data.data.readAt
      } catch (error) {
        if (id === this.requestId) this.error = resolveApiErrorMessage(error, 'Paraşüt belgeleri alınamadı.')
      } finally { if (id === this.requestId) this.loading = false }
    },
    applyFilters() {
      const supplierId = String(this.filters.supplierId || '').trim()
      if (supplierId && !/^[1-9]\d*$/.test(supplierId)) {
        this.error = 'Geçerli bir cari hesap ID girin.'

        return
      }
      this.appliedFilters = {
        [this.isSales ? 'contactId' : 'supplierId']: supplierId || undefined,
        issueDate: this.filters.issueDate || undefined,
        itemType: this.filters.itemType || undefined,
      }
      this.page = 1
      this.refresh()
    },
    changeSource(source) {
      if (!['bills', 'salaries', 'taxes'].includes(source) || source === this.expenseSource) return
      this.expenseSource = source
      this.filters = { supplierId: '', issueDate: '', itemType: null }
      this.appliedFilters = {}
      this.page = 1
      this.refresh()
    },
    changePage(page) { if (!this.loading && page !== this.page) { this.page = page; this.refresh() } },
    changeSize(size) { if (!this.loading && [10, 25, 50, 100].includes(size) && size !== this.size) { this.size = size; this.page = 1; this.refresh() } },
    formatReadAt: formatParasutReadAt,
    formatDay: formatParasutDay,
    formatMoney: formatParasutMoney,
    paymentStatusLabel,
    documentTypeLabel,
  },
}
</script>

<style scoped>
.supplier-toolbar { display: flex; gap: 14px; flex-wrap: wrap; padding: 16px; align-items: center; }
.supplier-toolbar .v-input { flex: 0 1 230px; }
.supplier-description { max-width: 340px; overflow-wrap: anywhere; }
.money { font-variant-numeric: tabular-nums; white-space: nowrap; }
.document-tags { display: flex; gap: 6px; flex-wrap: wrap; padding: 4px 0; }
@media (max-width: 600px) { .supplier-toolbar .v-input { flex-basis: 100%; } }
</style>
